/**
 * Widget settings — every config section on one page, one tab per group,
 * with the live preview and the draft → validate → publish bar. The
 * onboarding screens edit subsets of the same draft through the same section
 * components; this is the complete editor.
 *
 * The open tab is in the URL (`/config?tab=app-handoff`), so other sections
 * and screens can link straight to the one control that owns a setting.
 *
 * `?tab=` keys match the tab labels (config/tabs.ts; third live review,
 * 2026-09-27); the older keys still work:
 *   branding          Branding
 *   copy              Copy
 *   scope             Scope
 *   safety            Safety
 *   contact-form      Contact form     also: capture, form, contact
 *   after-diagnosis   After diagnosis  also: routing, after, routing-actions
 *   placement         Placement
 *   app-handoff       App handoff      also: handoff, app
 * An unknown key opens the first tab with a short note saying so, instead of
 * silently showing Branding (second audit, 2026-09-27).
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useDraft } from '../config/draft';
import { PublishBar } from '../config/PublishBar';
import { EditorWithPreview } from '../config/PreviewDock';
import { BrandingSection } from '../config/sections/BrandingSection';
import { CaptureSection } from '../config/sections/CaptureSection';
import { CopySection } from '../config/sections/CopySection';
import { HandoffSection } from '../config/sections/HandoffSection';
import { PlacementSection } from '../config/sections/PlacementSection';
import { RoutingSection } from '../config/sections/RoutingSection';
import { SafetySection } from '../config/sections/SafetySection';
import { ScopeSection } from '../config/sections/ScopeSection';
import { WithSiteDraft } from '../config/WithSiteDraft';
import type { ConfigGroup } from '../config/model';
import { useRouter } from '../router';
import { TAB_ALIASES, tabSlug } from '../config/tabs';
import { useSites } from '../state/sites';
import { PageHead } from '../ui/kit';

/**
 * `paths`: the draft paths whose problems count against the tab. Mostly the
 * group itself; the app button's label (`copy.cta_app`) is edited on the App
 * handoff tab, so its problems are counted there and not under Copy.
 */
export const CONFIG_TABS: Array<{ group: ConfigGroup; label: string; render: () => ReactNode; owns: (path: string) => boolean }> = [
  { group: 'branding', label: 'Branding', render: () => <BrandingSection />, owns: (p) => under(p, 'branding') },
  { group: 'copy', label: 'Copy', render: () => <CopySection />, owns: (p) => under(p, 'copy') && !under(p, 'copy.cta_app') },
  { group: 'scope', label: 'Scope', render: () => <ScopeSection />, owns: (p) => under(p, 'scope') },
  { group: 'safety', label: 'Safety', render: () => <SafetySection />, owns: (p) => under(p, 'safety') },
  { group: 'capture', label: 'Contact form', render: () => <CaptureSection />, owns: (p) => under(p, 'capture') },
  { group: 'routing', label: 'After diagnosis', render: () => <RoutingSection />, owns: (p) => under(p, 'routing') },
  { group: 'placement', label: 'Placement', render: () => <PlacementSection />, owns: (p) => under(p, 'placement') },
  { group: 'handoff', label: 'App handoff', render: () => <HandoffSection />, owns: (p) => under(p, 'handoff') || under(p, 'copy.cta_app') },
];

function under(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}.`);
}

function isTab(value: string | null): value is ConfigGroup {
  return CONFIG_TABS.some((t) => t.group === value);
}

export { TAB_ALIASES };

/** `?tab=` → the tab it names (its label key, group key or an alias, any case), or null. */
export function resolveTab(value: string | null): ConfigGroup | null {
  if (!value) return null;
  const key = value.trim().toLowerCase();
  const bySlug = CONFIG_TABS.find((t) => tabSlug(t.group) === key);
  if (bySlug) return bySlug.group;
  if (isTab(key)) return key;
  return TAB_ALIASES[key] ?? null;
}

/**
 * The tab row scrolls sideways when it does not fit (a phone, a narrow
 * window) instead of wrapping onto a second row; a fade on the clipped
 * edge says there is more.
 */
function useOverflowEdges(ref: React.RefObject<HTMLDivElement | null>) {
  const [edges, setEdges] = useState({ start: false, end: false });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const start = el.scrollLeft > 1;
      const end = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setEdges((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
    };
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', measure);
      ro?.disconnect();
    };
  }, [ref]);
  return edges;
}

function Body() {
  const d = useDraft();
  const { current } = useSites();
  const { search } = useRouter();
  const fromUrl = search.get('tab');
  const [tab, setTabState] = useState<ConfigGroup>(resolveTab(fromUrl) ?? CONFIG_TABS[0]!.group);
  // A `?tab=` nobody knows: say so once, next to the first tab, until another tab is chosen.
  const [unknownTab, setUnknownTab] = useState<string | null>(fromUrl && !resolveTab(fromUrl) ? fromUrl : null);
  const list = useRef<HTMLDivElement>(null);
  const edges = useOverflowEdges(list);

  // A link elsewhere (`/config?tab=handoff`) switches the open tab — every
  // navigation, even to the tab named last time (each one is a new `search`).
  // Done during render, not in an effect: the mount-time effect re-applied the
  // URL's tab after the first render, so a tab chosen before that deferred
  // effect ran was switched straight back.
  const [seenSearch, setSeenSearch] = useState(search);
  if (seenSearch !== search) {
    setSeenSearch(search);
    const resolved = resolveTab(fromUrl);
    if (resolved) {
      setTabState(resolved);
      setUnknownTab(null);
    } else if (fromUrl) {
      setUnknownTab(fromUrl);
    }
  }

  // Keep the open tab in view inside a scrolled tab row (sideways only).
  useEffect(() => {
    const row = list.current;
    const el = document.getElementById(`tab-${tab}`);
    if (!row || !el) return;
    if (el.offsetLeft < row.scrollLeft) row.scrollLeft = el.offsetLeft - 24;
    else if (el.offsetLeft + el.offsetWidth > row.scrollLeft + row.clientWidth) row.scrollLeft = el.offsetLeft + el.offsetWidth - row.clientWidth + 24;
  }, [tab]);

  // The URL follows the tab without a navigation (no scroll jump), so a
  // reload or a shared link reopens it.
  const setTab = (group: ConfigGroup) => {
    setTabState(group);
    setUnknownTab(null);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tabSlug(group));
    window.history.replaceState(window.history.state, '', url);
  };

  const active = CONFIG_TABS.find((t) => t.group === tab) ?? CONFIG_TABS[0]!;
  const errorsIn = (owns: (p: string) => boolean) => d.issues.filter((i) => owns(i.path)).length;
  const siteKey = current?.test?.site_key ?? current?.live.site_key ?? '';

  return (
    <div>
      <PageHead title="Widget settings" lede={<>Settings for <strong style={{ color: 'var(--ink)' }}>{current?.live.label}</strong>. Save a draft as often as you like; nothing changes on your site until you publish.</>} />
      <div className={`tabs-wrap${edges.start ? ' more-start' : ''}${edges.end ? ' more-end' : ''}`}>
        <div className="tabs" role="tablist" aria-label="Settings sections" ref={list}>
          {CONFIG_TABS.map((t) => {
            const errs = errorsIn(t.owns);
            return (
              <button
                key={t.group}
                type="button"
                role="tab"
                id={`tab-${t.group}`}
                aria-selected={tab === t.group}
                aria-controls="tabpanel"
                tabIndex={tab === t.group ? 0 : -1}
                onClick={() => setTab(t.group)}
                onKeyDown={(e) => {
                  const i = CONFIG_TABS.findIndex((x) => x.group === tab);
                  let next: (typeof CONFIG_TABS)[number] | undefined;
                  if (e.key === 'ArrowRight') next = CONFIG_TABS[(i + 1) % CONFIG_TABS.length];
                  else if (e.key === 'ArrowLeft') next = CONFIG_TABS[(i + CONFIG_TABS.length - 1) % CONFIG_TABS.length];
                  else if (e.key === 'Home') next = CONFIG_TABS[0];
                  else if (e.key === 'End') next = CONFIG_TABS[CONFIG_TABS.length - 1];
                  if (!next) return;
                  e.preventDefault();
                  setTab(next.group);
                  document.getElementById(`tab-${next.group}`)?.focus();
                }}
              >
                {t.label}
                {errs ? <span className="badge" aria-label={`${errs} problems`}>{errs}</span> : null}
                {!errs && (d.dirtyGroups.includes(t.group) || (t.group === 'handoff' && d.base.copy.cta_app !== d.edited.copy.cta_app)) ?  <span className="dirty" aria-label="unsaved changes" /> : null}
              </button>
            );
          })}
        </div>
      </div>
      {unknownTab ? (
        <p className="small muted" role="note" style={{ margin: '0 0 var(--s3)' }}>
          There is no “{unknownTab}” section, so this opened {CONFIG_TABS[0]!.label}. Pick a section above.
        </p>
      ) : null}
      <EditorWithPreview siteKey={siteKey}>
        <div role="tabpanel" id="tabpanel" aria-labelledby={`tab-${active.group}`}>
          {active.render()}
        </div>
      </EditorWithPreview>
      <PublishBar hasTestKey={Boolean(current?.test)} />
    </div>
  );
}

export function ConfigEditor() {
  return (
    <WithSiteDraft>
      <Body />
    </WithSiteDraft>
  );
}
